import { SchedulingLeg, SchedulingHistoryRecord, LegScheduleUpdate, LCNextStep, ScheduleStatus } from '../../../types/scheduling';

interface SchedulerExpandedPanelProps {
  row: SchedulingLeg;
  historyRecords: SchedulingHistoryRecord[];
  historyLoading: boolean;
  onUpdateSchedule: (pronumber: number, update: LegScheduleUpdate) => void;
  showStatus?: boolean;
}

export default function SchedulerExpandedPanel({
  row,
  historyRecords,
  historyLoading,
  onUpdateSchedule,
  showStatus = false,
}: SchedulerExpandedPanelProps) {
  return (
    <div className="p-4 text-xs h-[250px] overflow-y-auto">
      <div className="flex gap-6 h-full">
        {/* Main content area */}
        <div className="flex-1 h-full flex flex-col">
          {/* Comments Section - Side by Side (with Status on LC) */}
          <div className="flex gap-4 flex-1 min-h-0">
            {/* Status dropdown (LC only) */}
            {showStatus && (
              <div className="w-36 flex-shrink-0">
                <div className="font-semibold text-heading mb-1">Status</div>
                <select
                  className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-heading"
                  value={row.lc_next_step || (
                    row.schedule_status === 'PENDING' ? 'PENDING_LC' :
                    row.schedule_status === 'HOLD' ? 'HOLD' :
                    row.schedule_status === 'PENDING_CUSTOMER' ? 'PENDING_CUSTOMER' : ''
                  )}
                  onChange={(e) => {
                    const newValue = (e.target.value || null) as LCNextStep | null;
                    let newStatus: ScheduleStatus | null = null;
                    if (newValue === 'PENDING_LC') newStatus = 'PENDING';
                    else if (newValue === 'PENDING_CUSTOMER') newStatus = 'PENDING_CUSTOMER';
                    else if (newValue === 'HOLD') newStatus = 'HOLD';

                    onUpdateSchedule(row.pronumber, {
                      lc_next_step: newValue,
                      ...(newStatus && { schedule_status: newStatus }),
                    });
                  }}
                >
                  <option value="">-- Select --</option>
                  <option value="PENDING_LC">Pending LC</option>
                  <option value="PENDING_CUSTOMER">Pending Customer</option>
                  <option value="HOLD">Hold</option>
                </select>
              </div>
            )}
            {/* Scheduling Comment (Editable) */}
            <div className="flex-1 flex flex-col">
              <div className="font-semibold text-heading mb-1">Scheduling Comment</div>
              <textarea
                className="w-full flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-heading resize-none overflow-y-auto"
                defaultValue={row.scheduling_comments || ''}
                placeholder="Add scheduling comments..."
                onBlur={(e) => {
                  if (e.target.value !== (row.scheduling_comments || '')) {
                    onUpdateSchedule(row.pronumber, {
                      scheduling_comments: e.target.value || null,
                    });
                  }
                }}
              />
            </div>
            {/* Pro Comments (Read-only) */}
            <div className="flex-1 flex flex-col">
              <div className="font-semibold text-heading mb-1">Pro Comments</div>
              <div className="w-full flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 text-heading overflow-y-auto whitespace-pre-wrap">
                {row.pro_comments || <span className="text-gray-400">No comments</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Right sidebar: Scheduling History */}
        <div className="w-56 border-l border-gray-200 dark:border-gray-700 pl-4 flex-shrink-0 h-full overflow-y-auto">
          <div className="font-semibold text-heading mb-2">Scheduling History</div>
          {historyLoading ? (
            <div className="text-muted text-xs">Loading...</div>
          ) : historyRecords.length === 0 ? (
            <div className="text-muted text-xs">No history</div>
          ) : (
            <div className="space-y-0.5">
              {(() => {
                let hasPrepull = false;
                let hasDelivery = false;

                const filteredHistory = historyRecords.filter(
                  record => record.action_type !== 'CREATE' &&
                            record.field_name !== 'created' &&
                            record.field_name !== 'lc_next_step' &&
                            record.field_name !== 'scheduling_comments'
                );

                return filteredHistory.map((record) => {
                  if (record.field_name === 'proposed_prepull_date') {
                    hasPrepull = true;
                  } else if (record.field_name === 'proposed_appt_date') {
                    hasDelivery = true;
                  }

                  let prefix = '';
                  if (record.field_name === 'proposed_prepull_date') {
                    prefix = 'PrePull';
                  } else if (record.field_name === 'proposed_appt_date') {
                    prefix = 'Delivery';
                  } else if (record.field_name === 'schedule_status') {
                    if (hasPrepull && hasDelivery) prefix = 'PrePull/Delivery';
                    else if (hasDelivery) prefix = 'Delivery';
                    else if (hasPrepull) prefix = 'PrePull';
                  }

                  const toCamelCase = (str: string | null) => {
                    if (!str) return '-';
                    return str.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  };

                  const formatDateValue = (dateStr: string | null) => {
                    if (!dateStr) return '-';
                    const d = new Date(dateStr);
                    if (isNaN(d.getTime())) return dateStr;
                    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
                  };

                  let displayValue = '';
                  if (record.field_name === 'proposed_prepull_date' || record.field_name === 'proposed_appt_date') {
                    displayValue = formatDateValue(record.new_value);
                  } else if (record.field_name === 'schedule_status') {
                    if (record.new_value === 'PENDING_APPT' && hasPrepull && !hasDelivery) {
                      displayValue = 'Finalized';
                    } else {
                      displayValue = toCamelCase(record.new_value);
                    }
                  } else {
                    displayValue = record.new_value || '-';
                  }

                  return (
                    <div key={record.id} className="text-gray-600 dark:text-gray-400 text-xs leading-tight">
                      {prefix ? `${prefix} - ` : ''}{displayValue}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
